import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    clearCanvas,
    drawOnFrameText,
    drawCreatorText,
    drawEffect,
    drawName,
    drawScale,
    drawStat,
    drawStatText,
    drawSetId,
    drawSticker,
    getFinishIterator,
    drawTypeAbility,
    getEffectFontAndCoordinate,
    drawAsset,
    getLayoutDrawFunction,
    drawLinkRatingText,
    drawPredefinedMark,
    setTextStyle,
    drawPasswordText,
    drawLimitedEditionMark,
    baseDrawLinkArrowMap,
    baseDrawLinkMapFoil,
} from 'src/draw';
import {
    CanvasConst,
    MasterSeriesCanvas,
    NO_ATTRIBUTE,
    getArtCanvasCoordinate,
    getDefaultCardOpacity,
    PendulumEffectFontData,
    PendulumEffectCoordinateMap,
    FinishMap,
    ArtFinishMap,
    CardOpacity,
    DEFAULT_BASE_FILL_COLOR,
    DEFAULT_EFFECT_NORMAL_SIZE,
    DEFAULT_PENDULUM_EFFECT_NORMAL_SIZE,
    PendulumNormalFontData,
    PendulumSizeMap,
    PendulumSize,
    HALF_SCALE_WIDTH_OFFSET,
    ArrowPositionMap,
    parseOffset,
    LINK_ON_TOP_ALWAYS,
    LINK_ON_TOP_ACTIVATED_ONLY,
    LINK_ON_TOP_NEVER,
    getCardFormatMode,
} from 'src/model';
import {
    checkDiplayLinkRating,
    checkLightFooter,
    checkLightHeader,
    checkLink,
    checkMonster,
    checkNormal,
    checkSpeedSkill,
    checkXyz,
    createCanvas,
    resolveFrameStyle,
    resolveNameStyle,
} from 'src/util';
import { useCard } from '../use-card';
import { prepareStyle } from './prepare-style';
import { LanguageDataDictionary } from '../use-i18n';
import { notification } from 'antd';

export type ExportCallbackParameter = {
    isPendulum: boolean,
    opacity: Partial<CardOpacity>,
    pendulumSize: PendulumSize,
    onError: (e: unknown) => void,
    onSuccess: () => void,
    isRelevant: () => boolean,
};

const {
    height: CanvasHeight,
    width: CanvasWidth,
    finishTypeCardWidth,
    finishTypeCardHeight,
    stickerX,
    stickerY,
    stickerSize,
} = CanvasConst;
type DrawerProp = {
    imageChangeCount: number,
    isInitializing: boolean,
    language: LanguageDataDictionary,
    globalScale: number,
};
type DrawingPipeline = {
    name: string,
    order: number,
    rerun: number,
    instructor: () => Promise<unknown>,
    result: Promise<unknown>,
};
/**
 * To ensure correct layer order, each efffect that involve asynchronous image drawing will register an operation in `drawingPipeline` instead of immediately draw their part, these operations will be iterated sequentially by another effect when export.
 */
export const useMasterSeriDrawer = (active: boolean, canvasMap: MasterSeriesCanvas, props: DrawerProp) => {
    const {
        card,
    } = useCard();
    const {
        artworkCanvasRef,
        attributeCanvasRef,
        backgroundCanvasRef,
        cardIconCanvasRef,
        creatorCanvasRef,
        effectCanvasRef,
        exportCanvasRef,
        finishCanvasRef,
        frameCanvasRef,
        iconImageCanvasRef,
        lightboxRef,
        nameCanvasRef,
        overlayCanvasRef,
        passwordCanvasRef,
        pendulumEffectCanvasRef,
        pendulumScaleCanvasRef,
        previewCanvasRef,
        setIdCanvasRef,
        statCanvasRef,
        stickerCanvasRef,
        typeCanvasRef,
    } = canvasMap;
    const {
        format, region,
        legacyTemplate,
        hasBackground, backgroundType,
        overlay, overlayData, overlaySource, overlayType,
        iconImage, iconImageData, iconImageSource,
        frame,
        coordinateMap,
        foil, finish, artFinish, otherFinish, opacity,
        name, nameStyle, nameStyleType,
        effectTextStyle, pendulumTextStyle, typeTextStyle, statTextStyle, otherTextStyle,
        effect,
        effectStyle,
        typeAbility,
        isPendulum, pendulumFrame, pendulumRightFrame, pendulumEffect, pendulumScaleBlue, pendulumScaleRed, pendulumStyle, pendulumSize,
        leftFrame, rightFrame,
        dyeList,
        atk, def, linkMap, linkRating,
        attribute,
        attributeImageSource,
        cardIcon, subFamily, star, starAlignment, starList,
        setId,
        password, creator, sticker,
        isLegacyCard,
        isFirstEdition, isDuelTerminalCard, isSpeedCard, isLimitedEdition,
        cornerText, hasCornerText,
        firstEditionText,
        furiganaHelper,
        flag,
    } = card;
    const typographyFormat = getCardFormatMode(format, region);
    const textFuriganaHelper = typographyFormat === 'sc' ? false : furiganaHelper;
    const hasOverlay = (overlaySource === 'online' && overlay.trim() !== '')
        || (overlaySource === 'offline' && overlayData.trim() !== '');
    const hasIconImage = (iconImageSource === 'online' && iconImage.trim() !== '')
        || (iconImageSource === 'offline' && iconImageData.trim() !== '');

    const drawingPipeline = useRef<Record<string, DrawingPipeline>>({
        frame: {
            name: 'frame',
            order: 1,
            rerun: 0,
            instructor: () => Promise.resolve(),
            result: Promise.resolve(),
        },
        attribute: {
            name: 'attribute',
            order: 2,
            rerun: 0,
            instructor: () => Promise.resolve(),
            result: Promise.resolve(),
        },
        sticker: {
            name: 'sticker',
            order: 3,
            rerun: 0,
            instructor: () => Promise.resolve(),
            result: Promise.resolve(),
        },
        creator: {
            name: 'creator',
            order: 4,
            rerun: 0,
            instructor: () => Promise.resolve(),
            result: Promise.resolve(),
        },
        name: {
            name: 'name',
            order: 5,
            rerun: 0,
            instructor: () => Promise.resolve(),
            result: Promise.resolve(),
        },
        effect: {
            name: 'effect',
            order: 6,
            rerun: 0,
            instructor: () => Promise.resolve(),
            result: Promise.resolve(),
        },
        pendulumEffect: {
            name: 'pendulumEffect',
            order: 7,
            rerun: 0,
            instructor: () => Promise.resolve(),
            result: Promise.resolve(),
        },
        otherText: {
            name: 'otherText',
            order: 7,
            rerun: 0,
            instructor: () => Promise.resolve(),
            result: Promise.resolve(),
        },
        overlay: {
            name: 'overlay',
            order: 9,
            rerun: 0,
            instructor: () => Promise.resolve(),
            result: Promise.resolve(),
        },
    });

    const effectBackground = effectStyle.background;
    const pendulumEffectBackground = pendulumStyle.background;
    const effectMinLine = effectStyle.minLine;
    const pendulumEffectMinLine = pendulumStyle.minLine;
    const effectJustifyRatio = effectStyle.justifyRatio;
    const pendulumEffectJustifyRatio = pendulumStyle.justifyRatio;
    const bottomFrame = pendulumFrame === 'auto'
        ? isPendulum
            ? 'spell'
            : frame
        : pendulumFrame;

    const condenseTolerant = effectStyle?.condenseTolerant;

    const isNormal = checkNormal(card);
    const isXyz = checkXyz(card);
    const isLink = checkLink(card);
    const isMonster = checkMonster(card);
    const isSpeedSkill = checkSpeedSkill(card);

    const { body = 100, boundless } = opacity;
    const requireShadow = !!(body < 50 || boundless);
    const lightFooter = checkLightFooter(bottomFrame);
    const {
        bottomRightFrame,
    } = resolveFrameStyle(
        {
            frame,
            topLeftFrame: leftFrame,
            topRightFrame: rightFrame,
            bottomLeftFrame: pendulumFrame,
            bottomRightFrame: pendulumRightFrame,
            effectBackground: effectBackground,
            pendulumEffectBackground: pendulumEffectBackground,
        },
        isPendulum,
    );
    const lightRightFooter = checkLightFooter(bottomRightFrame);
    const lightHeader = checkLightHeader(frame);
    const {
        levelStyle,
        resolvedEffectTextStyle,
        resolvedPendulumEffectTextStyle,
        resolvedStatTextStyle,
        resolvedTypeTextStyle,
        resolvedOtherEffectTextStyle,
    } = useMemo(() => {
        return prepareStyle({
            lightFooter,
            lightHeader,
            requireShadow,
            effectTextStyle,
            pendulumTextStyle,
            otherTextStyle,
            statTextStyle,
            typeTextStyle,
        });
    }, [
        lightFooter,
        lightHeader,
        requireShadow,
        effectTextStyle,
        pendulumTextStyle,
        otherTextStyle,
        statTextStyle,
        typeTextStyle,
    ]);
    const foilDyeColor = dyeList[6];
    const effectBoxOffsetData = useMemo(
        () => parseOffset(coordinateMap.effectBox, CanvasConst.effectBox),
        [coordinateMap.effectBox],
    );

    const normalizedSubFamily = subFamily.toUpperCase();
    const normalizedTypeAbility = typeAbility.map(text => text.trim()).join(format === 'ocg' ? '／' : ' / ');
    const [
        showDefAndLinkFlag,
        linkRatingDisplayMode,
        hideInactiveLinkMarker,
        hideStatLabel,
        nameStarBelowImage,
        linkOnTop,
    ] = flag;

    /** One special case where we do not show link rating */
    const isLinkST = !isPendulum && !isMonster && isLink && !(atk || def);
    const showLinkRating = checkDiplayLinkRating(linkRatingDisplayMode, isLink) && !isLinkST;
    const showDefAndLink = showLinkRating && showDefAndLinkFlag;
    const statInEffect = !!(atk || def)
        || showLinkRating
        || !!(isPendulum && setId);
    const typeInEffect = normalizedTypeAbility.length > 0
        ? cardIcon === 'auto'
            ? (isMonster || isSpeedSkill)
            : cardIcon !== 'st'
        : false;
    const withBlueScale = !((pendulumScaleBlue ?? '') === '');
    const withRedScale = !((pendulumScaleRed ?? '') === '');
    const {
        isInitializing,
        imageChangeCount,
        language,
        globalScale,
    } = props;
    const readyToDraw = active && isInitializing === false;

    const loopFinish = useMemo(() => getFinishIterator(finish, FinishMap), [finish]);
    const loopArtFinish = useMemo(() => getFinishIterator([artFinish], ArtFinishMap), [artFinish]);
    const [, iconFinish, stickerFinish] = otherFinish;
    const getLinkLayer = useCallback(async (
        hideMarker: 'active' | 'inactive' | undefined,
    ) => {
        if (isLink) {
            const normalizedOpacity = { ...getDefaultCardOpacity(), ...opacity };
            const {
                artBorder: keepArtBorder,
                body: opacityBody,
                boundless,
            } = normalizedOpacity;
            const hasArtBorder = opacityBody > 0 ? true : keepArtBorder;
            const { ctx: linkArrowCtx, canvas: linkArrowCanvas } = createCanvas(CanvasWidth, CanvasHeight);
            if (linkArrowCtx) {
                await baseDrawLinkArrowMap(linkArrowCtx, 1, linkMap, isPendulum ? 'pendulum' : 'normal', boundless || !hasArtBorder);
                await baseDrawLinkMapFoil(linkArrowCtx, 1, foil, false, isPendulum ? 'pendulum' : 'normal', foilDyeColor);
                const removeMarkerList = ['1', '2', '3', '4', '5', '6', '7', '8', '9'].filter(entry => {
                    if (hideInactiveLinkMarker || hideMarker === 'inactive') return !linkMap.includes(entry);
                    if (hideMarker === 'active') return linkMap.includes(entry);
                    return false;
                });
                for (let cnt = 0; cnt < removeMarkerList.length; cnt++) {
                    const targetIndex = parseInt(removeMarkerList[cnt]);
                    const target = ArrowPositionMap[isPendulum ? 'pendulum' : 'normal'][targetIndex - 1];
                    if (target) linkArrowCtx.clearRect(target.left, target.top, target.width, target.height);
                }
                return linkArrowCanvas;
            }
        }
        return null;
    }, [foil, foilDyeColor, hideInactiveLinkMarker, isLink, isPendulum, linkMap, opacity]);

    /** DRAW NAME */
    useEffect(() => {
        if (!readyToDraw) return;
        drawingPipeline.current.name.rerun += 1;
        drawingPipeline.current.name.instructor = async () => {
            const ctx = nameCanvasRef.current?.getContext('2d');

            if (!clearCanvas(ctx) || !nameCanvasRef.current) return;

            await drawName(
                nameCanvasRef.current,
                ctx,
                name,
                format === 'tcg' ? 63 : 68, 116,
                attribute === NO_ATTRIBUTE
                    ? (format === 'tcg' ? 688 : 674)
                    : (format === 'tcg' ? 608 : 598),
                resolveNameStyle({ format, region, frame, nameStyle, nameStyleType, foil }),
                { isSpeedSkill, format, frame, furiganaHelper: textFuriganaHelper, globalScale },
            );
        };
    }, [
        readyToDraw,
        globalScale,
        attribute,
        foil,
        format,
        frame,
        furiganaHelper,
        isSpeedSkill,
        name,
        nameCanvasRef,
        nameStyle,
        nameStyleType,
        region,
        textFuriganaHelper,
    ]);
    const normalizedBoundless = { ...getDefaultCardOpacity(), ...opacity }.boundless;
    const drawNameStarBelowImage = useMemo(
        () => {
            if (nameStarBelowImage && normalizedBoundless) return [
                readyToDraw,
                globalScale,
                nameStarBelowImage,
                attribute,
                foil,
                format,
                frame,
                furiganaHelper,
                isSpeedSkill,
                name,
                JSON.stringify(nameStyle),
                nameStyleType,
            ].join(' ');
            return 0;
        },
        [
            readyToDraw,
            globalScale,
            normalizedBoundless,
            nameStarBelowImage,
            attribute,
            foil,
            format,
            frame,
            furiganaHelper,
            isSpeedSkill,
            name,
            nameStyle,
            nameStyleType,
        ]
    );
    const drawNameStarEagerly = normalizedBoundless && drawNameStarBelowImage;
    const drawLinkPosition = normalizedBoundless
        ? linkOnTop
        : LINK_ON_TOP_ALWAYS;

    const eagerlyDrawHistory = useRef(-1);
    /** DRAW CARD STRUCTURE */
    useEffect(() => {
        if (!readyToDraw) return;
        drawingPipeline.current.frame.rerun += 1;
        drawingPipeline.current.frame.instructor = async () => {
            const ctx = frameCanvasRef.current?.getContext('2d');
            const artworkCanvas = artworkCanvasRef.current;
            const backgroundCanvas = backgroundCanvasRef.current;
            const attributeCanvas = attributeCanvasRef.current;
            const overlayCanvas = overlayCanvasRef.current;

            if (!clearCanvas(ctx) || !frameCanvasRef.current) return;

            const normalizedOpacity = { ...getDefaultCardOpacity(), ...opacity };
            const {
                effectBox: keepEffectBox,
                artBorder: keepArtBorder,
                body: opacityBody,
                text: opacityEffect,
                boundless,
                baseFill,
                frameBorder,
            } = normalizedOpacity;
            /**
             * Layers:
             * * Base color
             * * Background
             * * Effect / Pend Effect background (for extended artwork)
             * * Artwork (for see-through body)
             * * Frame (or custom frame)
             * * Card border
             * * Background (for pendulum)
             * * Artwork (for pendulum)
             * * Name box frame
             * * Pendulum box frame
             * * Effect box
             * * Art box frame
             * * Foil
             * * Name box frame (for overframe)
             * * Artwork (for overframe)
             * * Pendulum box frame (for overframe)
             * * Effect box (for overframe)
             * * Predefined texts
             */

            /** Extremely weird bug in Chrome Mobile that make this frame draw twice and overlapping each other. The bug appear and disappear consistently with seemingly unrelated actions:
             * * Choose a finish type with overlay finish fixex the problem, but draw the same overlay manually won't fix it.
             * * Set default base fill to transparency fix the problem.
             * 
             * Because the issue happens on mobile with limited debug capability, we can't quite figure out what is the root cause, but put the fillRect call inside a promise is the only consistent way to resolve this issue.
             */
            const fillBaseColor = (
                ctx: CanvasRenderingContext2D,
                x: number, y: number,
                w: number, h: number,
                customFill?: string,
            ) => {
                return new Promise(resolve => {
                    setTimeout(() => {
                        ctx.fillStyle = hasBackground
                            ? (customFill ?? baseFill)
                            : DEFAULT_BASE_FILL_COLOR;
                        ctx.fillRect(x, y, w, h);
                        resolve(true);
                    }, 0);
                });
            };
            const hasArtBorder = opacityBody > 0 ? true : keepArtBorder;

            const {
                drawAttribute,
                drawBackground,
                drawCardArt,
                drawFrame,
                drawPendulumScaleIcon,
                drawStar,

                drawNameBackground,
                drawEffectBackground,

                drawFrameBorder,
                drawNameBorder,
                drawArtBorder,
                drawPendulumBorder,
                drawBorderPendulumFinish,
                drawEffectBorder,
                drawCardBorder,
                drawStatBorder,

                drawAttributeFinish,
                drawArtBorderFoil,
                drawEffectBorderFoil,
                drawPendulumBorderFoil,

                drawNameFinish,
                drawArtFinish,
                drawArtOverlayFinish,
                drawArtBorderFinish,
                drawPendulumArtBorderFinish,
                drawFrameFinish,
                drawFrameBackgroundFinish,
                drawOverlayFinish,
                drawCardBorderFinish,
            } = getLayoutDrawFunction({
                canvas: frameCanvasRef.current,
                artworkCanvas, backgroundCanvas, overlayCanvas, attributeCanvas,
                globalScale,
                coordinateMap,
                region,
                legacyTemplate,
                frame, leftFrame, pendulumFrame, rightFrame, pendulumRightFrame,
                dyeList,
                effectBackground, pendulumEffectBackground,
                hasBackground,
                hasOverlay, overlayType,
                backgroundType,
                attribute,
                attributeImageSource,
                cardIcon, star, starList,
                foil,
                pendulumSize,
                opacity: normalizedOpacity,
                isLink, isSpeedSkill, isXyz,
                isPendulum,
                pendulumFrameTypeMap: {
                    red: withRedScale ? 'normal' : 'scaleless',
                    blue: withBlueScale ? 'normal' : 'scaleless',
                },
                otherFinish,
                loopFinish,
                loopArtFinish,
            });
            const callDrawStar = async () => await drawStar({
                style: levelStyle,
                starAlignment,
                iconImage: hasIconImage ? iconImageCanvasRef.current : undefined,
            });

            /** Fill color background so the card is not see-through even with transparent artwork */
            await fillBaseColor(ctx, 0, 0, globalScale * CanvasWidth, globalScale * CanvasHeight);

            /** artOnCard canvas ensure the art is drawn correct size and position on the actual card, overlay for art is applied here */
            const {
                canvas: artOnCardCanvas,
                ctx: artOnCardCtx,
            } = createCanvas(CanvasWidth * globalScale, CanvasHeight * globalScale);
            if (artworkCanvas && artOnCardCtx) drawCardArt(artOnCardCtx);
            await drawArtFinish(artOnCardCtx);
            await drawArtOverlayFinish(artOnCardCtx);

            /** Combine background and artwork together, overlay for background is applied here */
            const {
                canvas: combinedArtCanvas,
                ctx: combinedArtCtx,
            } = createCanvas(CanvasWidth * globalScale, CanvasHeight * globalScale);
            if (combinedArtCtx) await fillBaseColor(combinedArtCtx, 0, 0, globalScale * CanvasWidth, globalScale * CanvasHeight);
            if (backgroundCanvas && combinedArtCtx) await drawBackground(combinedArtCtx, hasArtBorder);
            /** Fill effect background here, bascially create a sandwich: opaque effect background => artwork => transparent effect background. */
            if (backgroundType === 'strict' && opacityEffect < 100 && combinedArtCtx && keepEffectBox) {
                await drawEffectBackground({
                    externalCtx: combinedArtCtx,
                    blendWithBackground: false,
                    withPendulum: isPendulum,
                });
            }
            if (!boundless && combinedArtCtx) combinedArtCtx.drawImage(artOnCardCanvas, 0, 0);

            /** @summary Draw the overall layout */
            /** Start with artwork at the bottom, then main frame, then outer card border. */
            ctx.drawImage(combinedArtCanvas, 0, 0);
            await drawFrame();
            if (backgroundType !== 'frame' || keepEffectBox) await drawCardBorder();
            await drawCardBorderFinish();

            /** @summary Draw NON-PENDULUM non-boundless card layout */
            if (!isPendulum) {
                if (!boundless) {
                    await drawNameBackground();
                }
                if (!boundless && (keepEffectBox || !hasBackground)) {
                    await drawEffectBackground({});
                    await drawEffectBorder();
                    /** Foil DOES NOT contains shadow, so it relies on the shadow of the border below. */
                    await drawEffectBorderFoil();
                }
                await drawArtBorder();
                await drawArtBorderFoil();
                await drawArtBorderFinish();
            }

            /** @summary Draw PENDULUM-LIKE non-boundless card layout. */
            if (isPendulum && !boundless) {
                /** Since pendulum art boundary is wider, we cannot relies on the artwork under frame, instead we must draw the artwork again, this time with different size. */
                const {
                    artX,
                    artY,
                    artWidth,
                    ratio,
                } = getArtCanvasCoordinate(isPendulum, opacity, undefined, pendulumSize);
                ctx.drawImage(
                    combinedArtCanvas,
                    globalScale * artX, globalScale * artY,
                    globalScale * artWidth, globalScale * artWidth / ratio,
                    globalScale * artX, globalScale * artY,
                    globalScale * artWidth, globalScale * artWidth / ratio,
                );

                await drawNameBackground();
                if (keepEffectBox) {
                    await drawEffectBackground({ withPendulum: true });
                }

                /** Scale and pendulum border frame, these will be covered by extended artwork so we doesn't draw them if the artwork is boundless */
                await drawPendulumScaleIcon();
                /** Draw normal border first so we got the shadow ready. Again foiled border DOES NOT have shadow by their own. */
                if (keepEffectBox) {
                    await drawPendulumBorder(hasArtBorder);
                    await drawPendulumBorderFoil(hasArtBorder);
                    await drawPendulumArtBorderFinish();
                    if (hasArtBorder) await drawBorderPendulumFinish();
                }
            }

            if (!boundless) {
                await drawFrameFinish();
                await drawNameFinish();
            }
            if (hasArtBorder) await drawFrameBackgroundFinish();

            /** Boundless art behavior here. If rigid frame is off, card image will be placed on top of the art border. The extended card image is still below name (text only), level, attribute, effect (both card and pendulum) and other predefined texts. */
            if (boundless) {
                await drawNameBackground();
                await drawNameFinish();
                await drawNameBorder();

                /**
                 * Draw card name and stars eagerly here, so they lie below the boundless image
                 */
                if (drawNameStarEagerly) {
                    if (eagerlyDrawHistory.current !== drawingPipeline.current.name.rerun) {
                        eagerlyDrawHistory.current = drawingPipeline.current.name.rerun;
                        await drawingPipeline.current.name.instructor();
                    }
                    if (nameCanvasRef.current) ctx.drawImage(nameCanvasRef.current, 0, 0);
                    await callDrawStar();
                }
                if (isLink && !isPendulum) {
                    /** For link layout, the artwork is above the art border, but still below the link arrows */
                    await drawArtBorderFinish();
                } else if (isPendulum && keepEffectBox) {
                    if (hasArtBorder) {
                        await drawPendulumBorder(hasArtBorder);
                        await drawPendulumBorderFoil(hasArtBorder);
                    }
                    await drawPendulumArtBorderFinish();
                }
                if (drawLinkPosition === LINK_ON_TOP_NEVER || drawLinkPosition === LINK_ON_TOP_ACTIVATED_ONLY) {
                    const linkArrowCanvas = await getLinkLayer(drawLinkPosition === LINK_ON_TOP_NEVER
                        ? undefined
                        : 'active');
                    if (linkArrowCanvas) {
                        ctx.scale(globalScale, globalScale);
                        ctx.drawImage(linkArrowCanvas, 0, 0);
                        ctx.scale(1 / globalScale, 1 / globalScale);
                    }
                }

                const {
                    artX,
                    artY,
                    artWidth,
                    ratio,
                } = getArtCanvasCoordinate(isPendulum, opacity, 'full', pendulumSize);

                if (frameBorder) await drawFrameBorder();
                ctx.drawImage(
                    artOnCardCanvas,
                    globalScale * artX, globalScale * artY,
                    globalScale * artWidth, globalScale * artWidth / ratio,
                    globalScale * artX, globalScale * artY,
                    globalScale * artWidth, globalScale * artWidth / ratio,
                );
                if (!frameBorder) await drawFrameBorder();
                /** Redraw various part here because the extended artwork may overlap with those */
                if (isPendulum) {
                    if (keepEffectBox) {
                        await drawEffectBackground({ withPendulum: true });
                        await drawPendulumScaleIcon();
                        await drawPendulumBorder(false);
                        await drawPendulumBorderFoil(false);
                        await drawBorderPendulumFinish();
                    } else {
                        await drawPendulumScaleIcon();
                    }
                } else if (keepEffectBox) {
                    await drawEffectBackground({});
                    await drawEffectBorder();
                    await drawEffectBorderFoil();
                }
                await drawFrameFinish();
            }

            if (statInEffect) await drawStatBorder({
                color: '#000000',
                ...resolvedStatTextStyle,
            });

            if (!boundless) {
                await drawNameBorder();
                await drawFrameBorder();
            }
            try {
                await drawAttribute();
            } catch (e) {
                console.error('drawAttribute error', e);
            }
            await drawAttributeFinish();
            if (!drawNameStarEagerly) await callDrawStar();

            if (showLinkRating && statInEffect) {
                const resetStyle = setTextStyle({ ctx, ...resolvedStatTextStyle, globalScale });
                const normalizedLinkRating = typeof linkRating === 'string' && linkRating.length > 0
                    ? linkRating
                    : `${(Array.isArray(linkMap) ? linkMap.length : 0)}`;
                await drawLinkRatingText(
                    frameCanvasRef.current,
                    normalizedLinkRating,
                    resolvedStatTextStyle,
                    coordinateMap.effectBox ?? '',
                    !hideStatLabel,
                    globalScale,
                );
                resetStyle();
            }
            await drawPredefinedMark({
                bordered: (opacityBody < 50 || boundless) && !isPendulum,
                canvas: frameCanvasRef.current,
                globalScale,
                isDuelTerminalCard, isSpeedCard,
                isLink, isPendulum,
                offsetData: effectBoxOffsetData,
                textStyle: resolvedOtherEffectTextStyle,
                type: (lightFooter && !isPendulum) ? 'white' : 'black',
            });
            await drawOverlayFinish();
        };
    }, [
        readyToDraw,
        globalScale,
        artworkCanvasRef,
        attribute,
        attributeCanvasRef,
        attributeImageSource,
        backgroundCanvasRef,
        backgroundType,
        cardIcon,
        coordinateMap,
        dyeList,
        effectBackground,
        effectBoxOffsetData,
        finish,
        foil,
        format,
        frame,
        frameCanvasRef,
        hasBackground,
        hasIconImage,
        hasOverlay,
        hideStatLabel,
        iconImageCanvasRef,
        isDuelTerminalCard,
        isLink,
        isPendulum,
        isSpeedCard,
        isSpeedSkill,
        isXyz,
        leftFrame,
        legacyTemplate,
        levelStyle,
        lightFooter,
        linkMap,
        linkRating,
        loopArtFinish,
        loopFinish,
        nameCanvasRef,
        opacity,
        otherFinish,
        overlayCanvasRef,
        overlayType,
        pendulumEffectBackground,
        pendulumFrame,
        pendulumRightFrame,
        pendulumSize,
        region,
        resolvedOtherEffectTextStyle,
        resolvedStatTextStyle,
        rightFrame,
        showLinkRating,
        star,
        starAlignment,
        starList,
        statInEffect,
        drawNameStarEagerly,
        drawLinkPosition,
        withBlueScale,
        getLinkLayer,
        withRedScale,
        imageChangeCount, // Special dependency, do not remove even though it is not used in the effect itself
    ]);

    /** DRAW PENDULUM SCALE */
    useEffect(() => {
        if (!readyToDraw) return;
        const ctx = pendulumScaleCanvasRef.current?.getContext('2d');

        if (!clearCanvas(ctx)) return;
        if (isPendulum) {
            const { numberBlueX, numberRedX, numberY, fontSize } = PendulumSizeMap[pendulumSize];
            const offsetedBlueX = numberBlueX - effectBoxOffsetData.x;
            const offsetedRedX = numberRedX - effectBoxOffsetData.x;
            const restArgumentList = [numberY - effectBoxOffsetData.y, fontSize, globalScale, resolvedPendulumEffectTextStyle] as const;
            if ((pendulumScaleBlue ?? '') !== '') drawScale(ctx, pendulumScaleBlue, offsetedBlueX, ...restArgumentList);
            if ((pendulumScaleRed ?? '') !== '') drawScale(ctx, pendulumScaleRed, offsetedRedX, ...restArgumentList);
        }
    }, [
        readyToDraw,
        globalScale,
        effectBoxOffsetData,
        isPendulum,
        pendulumScaleBlue,
        pendulumScaleCanvasRef,
        pendulumScaleRed,
        pendulumSize,
        resolvedPendulumEffectTextStyle,
    ]);

    /** DRAW STAT (ATK / DEF / LINK RATING) */
    useEffect(() => {
        if (!readyToDraw) return;
        const ctx = statCanvasRef.current?.getContext('2d');

        if (!clearCanvas(ctx) || !statInEffect) return;

        const resetStyle = setTextStyle({ ctx, ...resolvedStatTextStyle, globalScale });
        // const hasAtk = !!atk;
        const hasDef = showLinkRating
            ? !!def && showDefAndLink
            : !!def;
        const hasLink = showLinkRating;
        if (atk) {
            const offset = (hasDef ? 168.75 : 0) + (hasLink ? 168.75 : 0) + effectBoxOffsetData.x;
            if (!hideStatLabel) drawStatText(ctx, 'ATK', 600.85 - offset, 1106 - effectBoxOffsetData.y, globalScale);
            drawStat(ctx, atk.trim(), 677.574 - offset, 1106.5 - effectBoxOffsetData.y, globalScale);
        }
        if (def && (!showLinkRating || showDefAndLink)) {
            const offset = (hasLink ? 168.75 : 0) + effectBoxOffsetData.x;
            if (!hideStatLabel) drawStatText(ctx, 'DEF', 600.85 - offset, 1106 - effectBoxOffsetData.y, globalScale);
            drawStat(ctx, def.trim(), 673.865 - offset, 1106.5 - effectBoxOffsetData.y, globalScale);
        }
        resetStyle();
    }, [
        atk,
        def,
        effectBoxOffsetData,
        globalScale,
        hideStatLabel,
        isMonster,
        readyToDraw,
        resolvedStatTextStyle,
        showDefAndLink,
        showLinkRating,
        statCanvasRef,
        statInEffect,
    ]);

    /** DRAW SET ID */
    useEffect(() => {
        if (!readyToDraw) return;
        const ctx = setIdCanvasRef.current?.getContext('2d');

        if (!clearCanvas(ctx)) return;

        drawSetId({
            ctx,
            value: setId,
            globalScale,
            isLink,
            isPendulum,
            withShadow: requireShadow && !isPendulum,
            offsetData: effectBoxOffsetData,
            format,
            lightFooter: lightRightFooter,
            textStyle: resolvedOtherEffectTextStyle,
        });
    }, [
        readyToDraw,
        globalScale,
        format,
        isLink,
        isPendulum,
        lightRightFooter,
        effectBoxOffsetData,
        setIdCanvasRef,
        setId,
        isSpeedSkill,
        bottomFrame,
        requireShadow,
        resolvedOtherEffectTextStyle,
    ]);

    /** DRAW FIRST EDITION NOTICE (1st Edition Text), CORNER TEXT AND PASSWORD */
    useEffect(() => {
        if (!readyToDraw) return;
        const ctx = passwordCanvasRef.current?.getContext('2d');

        drawingPipeline.current.otherText.rerun += 1;
        drawingPipeline.current.otherText.instructor = async () => {
            if (!clearCanvas(ctx)) return;

            const isNumberPassword = /^[0-9]*$/.test(password);
            const willOffset = isNumberPassword && isPendulum && isLink;
            const { rightEdge } = await drawPasswordText({
                ctx,
                globalScale,
                value: password,
                lightFooter,
                alignment: 'left',
                edgeOffset: (willOffset ? 80 : 0) * globalScale,
                format,
                hasShadow: bottomFrame === 'zarc' || requireShadow,
                textStyle: resolvedOtherEffectTextStyle,
                fontLevel: !isNumberPassword ? 1 : 0
            });
            const editionTextUseTopPosition = (isLegacyCard || !isNumberPassword) && !isPendulum;
            if (isFirstEdition && typographyFormat !== 'sc') {
                const willDrawFirstEdition = isPendulum
                    ? isNumberPassword ? true : false
                    : true;
                const left = editionTextUseTopPosition
                    ? isLink ? 151 : 89
                    : Math.max(rightEdge / globalScale + 14.813, 154.2) - (format === 'ocg' ? 7 : 0);
                const bottom = editionTextUseTopPosition
                    ? 871
                    : 1150.93;
                const bottomOffset = editionTextUseTopPosition
                    ? 0
                    : isSpeedSkill ? -2 : -1;
                const width = editionTextUseTopPosition
                    ? isPendulum
                        ? 683
                        : 475
                    : (isLink && isPendulum)
                        ? 130
                        : creator.trim().length === 0
                            ? 615
                            : 185;

                if (willDrawFirstEdition) drawOnFrameText({
                    ctx,
                    txt: firstEditionText,
                    edge: left,
                    baseline: bottom,
                    baselineOffset: bottomOffset,
                    width,
                    option: {
                        stroke: false,
                        globalScale,
                        textStyle: {
                            color: lightFooter ? '#ffffff' : '#000000',
                            ...resolvedOtherEffectTextStyle,
                        }
                    },
                });
            }
            if (hasCornerText) {
                const left = !editionTextUseTopPosition
                    ? isPendulum
                        ? 65
                        : isLink
                            ? 151
                            : 89
                    : Math.max(rightEdge / globalScale + 14.813, 154.2) - (format === 'ocg' ? 7 : 0);
                const bottom = !editionTextUseTopPosition
                    ? 871
                    : 1150.93;
                const bottomOffset = !editionTextUseTopPosition
                    ? isPendulum
                        ? pendulumSize === 'small'
                            ? -100
                            : -140
                        : 0
                    : isSpeedSkill ? -2 : -1;
                const width = !editionTextUseTopPosition
                    ? isPendulum
                        ? 683
                        : 475
                    : creator.trim().length === 0
                        ? 615
                        : 185;

                drawOnFrameText({
                    ctx,
                    txt: cornerText,
                    edge: left,
                    baseline: bottom,
                    baselineOffset: bottomOffset,
                    width,
                    option: {
                        stroke: false,
                        globalScale,
                        textStyle: {
                            color: lightFooter ? '#ffffff' : '#000000',
                            ...resolvedOtherEffectTextStyle,
                        }
                    },
                });
            }
        };
    }, [
        readyToDraw,
        globalScale,
        creator,
        bottomFrame,
        cornerText,
        firstEditionText,
        format,
        hasCornerText,
        isFirstEdition,
        isLegacyCard,
        isLink,
        isPendulum,
        isSpeedSkill,
        lightFooter,
        password,
        passwordCanvasRef,
        pendulumSize,
        requireShadow,
        resolvedOtherEffectTextStyle,
        typographyFormat,
    ]);

    /** DRAW CREATOR (COPYRIGHT) TEXT */
    useEffect(() => {
        if (!readyToDraw) return;

        const ctx = creatorCanvasRef.current?.getContext('2d');

        drawingPipeline.current.creator.rerun += 1;
        drawingPipeline.current.creator.instructor = async () => {
            if (!clearCanvas(ctx)) return;

            const normalizedOpacity = { ...getDefaultCardOpacity(), ...opacity };
            const {
                body: opacityBody,
                boundless,
            } = normalizedOpacity;
            const endOfCreatorText = await drawCreatorText({
                ctx: creatorCanvasRef.current?.getContext('2d'),
                format,
                value: creator,
                alignment: 'right',
                edgeOffset: (isLink && isPendulum) ? -20 : 0,
                widthOffset: ((isLink && isPendulum) ? -120 : 0) * globalScale,
                baselineOffset: isSpeedSkill ? -2 : 0,
                hasShadow: requireShadow,
                lightFooter: lightRightFooter,
                textStyle: resolvedOtherEffectTextStyle,
                globalScale,
            });
            const compactThreshold = (format === 'tcg' ? 390 : 350) * globalScale;
            const compactOffset = (format === 'tcg' ? 30 : 40) * globalScale;

            if (isLimitedEdition && creatorCanvasRef.current) {
                await drawLimitedEditionMark({
                    canvas: creatorCanvasRef.current,
                    ctx,
                    globalScale,
                    type: (lightFooter && !isPendulum) ? 'white' : 'black',
                    bordered: (opacityBody < 50 || boundless) && !isPendulum,
                    isLink, isPendulum,
                    widthOffset: (endOfCreatorText?.leftEdge ?? compactThreshold) < compactThreshold
                        ? compactOffset
                        : 0,
                    isLegacyCard,
                    textStyle: resolvedOtherEffectTextStyle,
                });
            }
        };
    }, [
        readyToDraw,
        globalScale,
        creator,
        creatorCanvasRef,
        effectCanvasRef,
        format,
        isLegacyCard,
        isLimitedEdition,
        isLink,
        isPendulum,
        isSpeedSkill,
        lightFooter,
        lightRightFooter,
        opacity,
        requireShadow,
        resolvedOtherEffectTextStyle,
    ]);

    /** DRAW STICKER */
    useEffect(() => {
        if (!readyToDraw) return;

        drawingPipeline.current.sticker.rerun += 1;
        drawingPipeline.current.sticker.instructor = async () => {
            const ctx = stickerCanvasRef.current?.getContext('2d');
            if (!clearCanvas(ctx)) return;
            const {
                canvas: stickerCanvas,
                ctx: stickerContext,
            } = createCanvas(CanvasWidth * globalScale, CanvasHeight * globalScale);
            await drawSticker({
                ctx: stickerContext,
                sticker,
                globalScale,
                x: stickerX,
                y: stickerY,
            });
            const normalizedStickerFinish = stickerFinish ?? 'normal';
            if (normalizedStickerFinish !== 'normal') {
                const loopStickerFinish = getFinishIterator([normalizedStickerFinish], ArtFinishMap);
                const {
                    canvas: stickerFinishCanvas,
                    ctx: stickerFinishContext,
                } = createCanvas(CanvasWidth * globalScale, CanvasHeight * globalScale);
                if (stickerFinishContext && stickerContext) {
                    stickerFinishContext.drawImage(stickerCanvas, 0, 0);
                    stickerFinishContext.scale(globalScale, globalScale);
                    await loopStickerFinish(
                        stickerFinishContext,
                        'art',
                        async (finishType) => {
                            return await drawAsset(
                                stickerFinishContext,
                                `finish/finish-typeart-${finishType}.png`,
                                stickerX - finishTypeCardWidth + stickerSize, stickerY - finishTypeCardHeight + stickerSize,
                            );
                        },
                    );
                    stickerContext.globalCompositeOperation = 'source-in';
                    stickerContext.drawImage(stickerFinishCanvas, 0, 0);
                    ctx.drawImage(stickerCanvas, 0, 0);
                }
            } else {
                ctx.drawImage(stickerCanvas, 0, 0);
            }
        };
    }, [readyToDraw, globalScale, sticker, stickerFinish, stickerCanvasRef, loopArtFinish]);

    /** DRAW CARD EFFECT + TYPE ABILITY */
    useEffect(() => {
        if (!readyToDraw) return;

        const ctx = effectCanvasRef.current?.getContext('2d');
        const typeCtx = typeCanvasRef.current?.getContext('2d');

        drawingPipeline.current.effect.rerun += 1;
        drawingPipeline.current.effect.instructor = async () => {
            if (!clearCanvas(ctx) || !clearCanvas(typeCtx)) return;

            const { condenseTolerant, upSize, fontStyle } = effectStyle ?? {};
            const customizeEffectStyle = effectTextStyle[0];
            const normalizedUpSize = customizeEffectStyle ? upSize : 0;
            const useItalic = customizeEffectStyle
                ? (isNormal && fontStyle === 'auto') || (format === 'tcg' && fontStyle === 'italic')
                : isNormal;
            const effectIndexSize = await drawEffect({
                ctx,
                content: effect,
                isNormal,
                useItalic,
                region,
                condenseTolerant,
                format,
                furiganaHelper: textFuriganaHelper,
                offsetData: effectBoxOffsetData,
                ...getEffectFontAndCoordinate({
                    format,
                    fontFormat: typographyFormat,
                    statInEffect,
                    typeInEffect,
                    useItalic,
                    frameType: (isPendulum && pendulumSize === 'large') ? 'pendulumLarge' : 'normal',
                }),
                textStyle: resolvedEffectTextStyle,
                option: {
                    forceRelaxCondenseLimit: DEFAULT_EFFECT_NORMAL_SIZE,
                    defaultSizeLevel: DEFAULT_EFFECT_NORMAL_SIZE - normalizedUpSize,
                    globalScale,
                    minLine: effectMinLine,
                    justifyRatio: effectJustifyRatio,
                },
            });
            const normalizedIconFinish = iconFinish ?? 'normal';
            const loopIconFinish = normalizedIconFinish !== 'normal' ? getFinishIterator([normalizedIconFinish], ArtFinishMap) : undefined;
            await drawTypeAbility({
                ctx: typeCtx,
                globalScale,
                format,
                fontFormat: typographyFormat,
                frame,
                furiganaHelper: textFuriganaHelper,
                isMonster,
                textStyle: resolvedTypeTextStyle,
                offsetData: typeInEffect
                    ? effectBoxOffsetData
                    : parseOffset(coordinateMap.starBox, CanvasConst.starBox),
                size: !typeInEffect
                    ? 'large'
                    : (isPendulum && pendulumSize === 'large')
                        ? (effectIndexSize <= DEFAULT_EFFECT_NORMAL_SIZE ? 'highPendulumNormal' : 'highPendulumSmaller')
                        : (effectIndexSize <= DEFAULT_EFFECT_NORMAL_SIZE ? 'normal' : 'smaller'),
                subFamily: normalizedSubFamily,
                typeAbility: normalizedTypeAbility,
                loopIconFinish,
            });
        };
    }, [
        readyToDraw,
        globalScale,
        effectBoxOffsetData,
        coordinateMap.starBox,
        effect,
        effectCanvasRef,
        effectJustifyRatio,
        effectMinLine,
        effectStyle,
        effectTextStyle,
        format,
        frame,
        furiganaHelper,
        iconFinish,
        isMonster,
        isNormal,
        isPendulum,
        normalizedSubFamily,
        normalizedTypeAbility,
        pendulumSize,
        region,
        resolvedEffectTextStyle,
        resolvedTypeTextStyle,
        statInEffect,
        typeCanvasRef,
        typeInEffect,
        textFuriganaHelper,
        typographyFormat,
    ]);

    /** DRAW PENDULUM EFFECT */
    useEffect(() => {
        if (!readyToDraw) return;
        const ctx = pendulumEffectCanvasRef.current?.getContext('2d');

        drawingPipeline.current.pendulumEffect.rerun += 1;
        drawingPipeline.current.pendulumEffect.instructor = async () => {
            if (!clearCanvas(ctx)) return;
            if (isPendulum) {
                const { upSize, fontStyle } = pendulumStyle ?? {};
                const customPendulumStyle = pendulumTextStyle[0];
                const normalizedUpSize = customPendulumStyle ? upSize : 0;
                const useItalic = customPendulumStyle ? (format === 'tcg' && fontStyle === 'italic') : false;
                const fontDataKey = `${typographyFormat}-${pendulumSize}`;
                const coordinateList = PendulumEffectCoordinateMap[
                    (withBlueScale && withRedScale) ? 'normal' : 'scaleless'
                ][
                    pendulumSize
                ];
                /** Account for half scaleless frame */
                const modifiedCoordinateList = (withBlueScale && withRedScale)
                    ? coordinateList
                    : coordinateList.map(({
                        trueEdge, trueWidth, ...rest
                    }) => ({
                        ...rest,
                        trueEdge: trueEdge + (withBlueScale ? HALF_SCALE_WIDTH_OFFSET : 0),
                        trueWidth: trueWidth
                            - (withBlueScale ? HALF_SCALE_WIDTH_OFFSET : 0)
                            - (withRedScale ? HALF_SCALE_WIDTH_OFFSET : 0),
                    }));
                await drawEffect({
                    ctx,
                    content: pendulumEffect,
                    isNormal: false,
                    useItalic: useItalic,
                    fontData: (useItalic
                        ? PendulumNormalFontData
                        : PendulumEffectFontData)[fontDataKey],
                    fontDataKey,
                    region,
                    textStyle: resolvedPendulumEffectTextStyle,
                    sizeList: modifiedCoordinateList,
                    condenseTolerant,
                    format,
                    furiganaHelper: textFuriganaHelper,
                    offsetData: effectBoxOffsetData,
                    option: {
                        forceRelaxCondenseLimit: DEFAULT_PENDULUM_EFFECT_NORMAL_SIZE,
                        defaultSizeLevel: DEFAULT_PENDULUM_EFFECT_NORMAL_SIZE - normalizedUpSize,
                        globalScale,
                        minLine: pendulumEffectMinLine,
                        justifyRatio: pendulumEffectJustifyRatio,
                    }
                });
            }
        };
    }, [
        readyToDraw,
        globalScale,
        condenseTolerant,
        effectBoxOffsetData,
        format,
        furiganaHelper,
        isPendulum,
        pendulumEffect,
        pendulumEffectCanvasRef,
        pendulumEffectJustifyRatio,
        pendulumEffectMinLine,
        pendulumSize,
        pendulumStyle,
        pendulumTextStyle,
        region,
        resolvedPendulumEffectTextStyle,
        withBlueScale,
        withRedScale,
        textFuriganaHelper,
        typographyFormat,
    ]);

    /** DRAW TOTAL OVERLAY */
    useEffect(() => {
        if (!readyToDraw) return;
        const ctx = finishCanvasRef.current?.getContext('2d');

        drawingPipeline.current.overlay.rerun += 1;
        drawingPipeline.current.overlay.instructor = async () => {
            if (!clearCanvas(ctx)) return;

            if (drawLinkPosition === LINK_ON_TOP_ALWAYS || drawLinkPosition === LINK_ON_TOP_ACTIVATED_ONLY) {
                const linkArrowCanvas = await getLinkLayer(drawLinkPosition ? 'inactive' : undefined);
                if (linkArrowCanvas) {
                    ctx.scale(globalScale, globalScale);
                    ctx.drawImage(linkArrowCanvas, 0, 0);
                    ctx.scale(1 / globalScale, 1 / globalScale);
                }
            }

            ctx.scale(globalScale, globalScale);
            await loopFinish(
                ctx,
                'total-overlay',
                overlayType => drawAsset(ctx, `finish/finish-${overlayType}-total-overlay.png`, 0, 0),
            );
            ctx.resetTransform();
        };
    }, [readyToDraw, globalScale, finishCanvasRef, loopFinish, getLinkLayer, drawLinkPosition]);


    /**
     * How it works:
     * * Individual effect add an instruction that promises to return the newest value, and a rerun count as its identifier.
     * * When the export actually run, it checks the history list.
     *  * If the history identifier is the same as the newest instruction, simply return the promise.
     *  * Else, run the instruction with the newest identifer, and write that identifier in history.
     * * Afteward, await for all the promises. Then process to draw.
     */
    const drawHistory = useRef<Record<string, number>>({});
    const onExport = useCallback(async (exportProps: ExportCallbackParameter) => {
        const {
            isRelevant,
            //     pendulumSize,
            //     isPendulum = false,
            //     opacity,
            onError,
            onSuccess,
        } = exportProps;
        const {
            canvas: operateCanvas,
            ctx: operateContext,
        } = createCanvas(CanvasWidth * globalScale, CanvasHeight * globalScale);

        if (operateCanvas && operateContext) {
            lightboxRef.current?.setBusy(() => true);
            /** Check for new instructions and run them */
            Object
                .values(drawingPipeline.current)
                .sort((l, r) => l.order - r.order)
                .forEach(({ instructor, rerun, name }) => {
                    if (
                        (rerun !== 0 && drawHistory.current[name] !== rerun)
                        || !drawHistory.current[name]
                    ) {
                        drawHistory.current[name] = rerun;

                        drawingPipeline.current[name].result = instructor();
                    }
                });
            /** Await for all instruction results, including the new just-ran instructions above and old results that haven't changed. */
            await Promise.all(Object
                .values(drawingPipeline.current)
                .map(({ result }) => {
                    return result;
                }))
                .catch(e => {
                    console.error(e);
                    /** Ensure it does not fire repeatedly */
                    const key = 'fail-to-draw-notification';
                    notification.close(key);
                    notification.error({
                        key,
                        message: language['error.draw.error.message'],
                        description: language['error.draw.error.description'],
                    });
                });

            const exportCanvas = exportCanvasRef.current;
            const exportCtx = exportCanvas?.getContext('2d');
            const lastError: { count: number, error: unknown } = { count: -1, error: null };
            if (exportCanvas && exportCtx && isRelevant()) {
                /** It is not worth to use promise all here, just let them go sequentially to avoid too many blob generating calls. */
                const layerRefList = [
                    { name: 'frameCanvas', canvas: frameCanvasRef },
                    { name: 'nameCanvas', canvas: nameCanvasRef },
                    { name: 'cardIconCanvas', canvas: cardIconCanvasRef },
                    { name: 'pendulumScaleCanvas', canvas: pendulumScaleCanvasRef },
                    { name: 'pendulumEffectCanvas', canvas: pendulumEffectCanvasRef },
                    { name: 'typeCanvas', canvas: typeCanvasRef },
                    { name: 'effectCanvas', canvas: effectCanvasRef },
                    { name: 'statCanvas', canvas: statCanvasRef },
                    { name: 'setIdCanvas', canvas: setIdCanvasRef },
                    { name: 'passwordCanvas', canvas: passwordCanvasRef },
                    { name: 'creatorCanvas', canvas: creatorCanvasRef },
                    { name: 'stickerCanvas', canvas: stickerCanvasRef },
                    { name: 'finishCanvas', canvas: finishCanvasRef },
                ];
                for (let cnt = 0; cnt < layerRefList.length; cnt++) {
                    const { canvas, name } = layerRefList[cnt];
                    const willDraw = drawNameStarEagerly
                        ? !(['nameLayer'].includes(name))
                        : true;
                    if (canvas.current && willDraw) {
                        operateContext.drawImage(canvas.current, 0, 0);
                    }
                }
                if (lastError.error) onError(lastError.error);
                clearCanvas(exportCtx);
                exportCtx.drawImage(operateCanvas, 0, 0);
                lightboxRef.current?.draw(operateCanvas);
                lightboxRef.current?.setBusy(() => false);
                previewCanvasRef.current?.getContext('2d')?.drawImage(
                    operateCanvas,
                    0,
                    0,
                    exportCanvas.width,
                    exportCanvas.height,
                    0,
                    0,
                    CanvasWidth,
                    CanvasHeight,
                );
            }
            if (!lastError.error) onSuccess();
        }
    }, [
        language,
        globalScale,
        cardIconCanvasRef,
        creatorCanvasRef,
        effectCanvasRef,
        exportCanvasRef,
        finishCanvasRef,
        frameCanvasRef,
        lightboxRef,
        nameCanvasRef,
        drawNameStarEagerly,
        passwordCanvasRef,
        pendulumEffectCanvasRef,
        pendulumScaleCanvasRef,
        previewCanvasRef,
        setIdCanvasRef,
        statCanvasRef,
        stickerCanvasRef,
        typeCanvasRef,
    ]);

    return {
        drawingPipeline,
        onExport,
    };
};
